import { useScrollReveal } from '@/hooks/useScrollReveal';

const ecosystemNodes = [
  {
    icon: 'ri-building-2-line',
    title: 'Warehouses',
    stat: '340+',
    statLabel: 'Facilities',
    description: 'Independent owners list idle freezing capacity',
    color: 'bg-secondary-500',
    colorLight: 'bg-secondary-50',
    colorBorder: 'border-secondary-200',
    colorText: 'text-secondary-600',
    colorTextBold: 'text-secondary-700',
    dotColor: 'bg-secondary-500',
  },
  {
    icon: 'ri-truck-line',
    title: 'Transporters',
    stat: '2,500+',
    statLabel: 'Vehicles',
    description: 'Independent fleet owners lease spare reefer space',
    color: 'bg-primary-500',
    colorLight: 'bg-primary-50',
    colorBorder: 'border-primary-200',
    colorText: 'text-primary-600',
    colorTextBold: 'text-primary-700',
    dotColor: 'bg-primary-500',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Buyers & Consumers',
    stat: '15K+',
    statLabel: 'Active Accounts',
    description: 'HORECA buyers and households trade with confidence',
    color: 'bg-accent-500',
    colorLight: 'bg-accent-50',
    colorBorder: 'border-accent-200',
    colorText: 'text-accent-600',
    colorTextBold: 'text-accent-700',
    dotColor: 'bg-accent-500',
  },
];

const modelBenefits = [
  {
    icon: 'ri-building-2-line',
    title: 'We Don\'t Own Warehouses',
    desc: 'We connect 340+ independently-owned cold storage facilities across India — not a single one of them built by us. Owners list their idle capacity; we match it to demand.',
    accentBg: 'bg-secondary-50',
    accentBorder: 'border-secondary-200',
    accentIconBg: 'bg-secondary-100',
    accentIconText: 'text-secondary-600',
  },
  {
    icon: 'ri-truck-line',
    title: 'We Don\'t Own a Fleet',
    desc: 'Our transport network is powered by 2,500+ independent reefer vehicle owners who lease spare space on our marketplace. We provide the matching engine, not the trucks.',
    accentBg: 'bg-primary-50',
    accentBorder: 'border-primary-200',
    accentIconBg: 'bg-primary-100',
    accentIconText: 'text-primary-600',
  },
  {
    icon: 'ri-store-2-line',
    title: 'We Don\'t Run a Store',
    desc: 'Wholesale buyers and retail customers transact directly with verified suppliers through our platform. We provide the trust layer — standardised specs, certifications, and traceability.',
    accentBg: 'bg-accent-50',
    accentBorder: 'border-accent-200',
    accentIconBg: 'bg-accent-100',
    accentIconText: 'text-accent-600',
  },
  {
    icon: 'ri-node-tree',
    title: 'We Are the Operating System',
    desc: 'Like how Alibaba connects factories to buyers, and Uber connects riders to drivers, Freshhlock connects every link in India\'s frozen supply chain — without owning any of the physical assets.',
    accentBg: 'bg-foreground-50',
    accentBorder: 'border-foreground-200',
    accentIconBg: 'bg-foreground-100',
    accentIconText: 'text-foreground-600',
  },
];

export default function OurModelSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section id="our-model" className="py-16 md:py-24 bg-background-50">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        {/* Section header */}
        <div className="text-center mb-14 md:mb-18">
          <span className="inline-block text-xs font-semibold text-primary-600 uppercase tracking-widest mb-4">
            Our Model
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
            Asset-Light, Network-First
          </h2>
          <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            The world\'s most valuable logistics companies don\'t own their core assets — they orchestrate them.
            Freshhlock applies the same proven marketplace model to India\'s frozen cold chain.
          </p>
        </div>

        {/* Network Visualization */}
        <div className="max-w-4xl mx-auto mb-16 md:mb-20">
          <div className="relative bg-gradient-to-br from-primary-50/80 via-secondary-50/40 to-accent-50/60 rounded-2xl border border-primary-200/30 p-8 md:p-12 shadow-sm">
            {/* Decorative grid lines */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
              backgroundImage: `linear-gradient(to right, oklch(var(--primary-400)) 1px, transparent 1px), linear-gradient(to bottom, oklch(var(--primary-400)) 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
            }}></div>

            {/* Hub */}
            <div className="relative z-10 flex flex-col items-center mb-10 md:mb-14">
              <div className="relative">
                {/* Outer pulse ring */}
                <div className="absolute inset-0 rounded-full bg-primary-400/20 animate-ping" style={{ animationDuration: '3s' }}></div>
                {/* Main hub circle */}
                <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-brand-600 to-brand-700 flex items-center justify-center shadow-lg shadow-primary-500/20">
                  <i className="ri-flashlight-line text-3xl md:text-4xl text-paper-50"></i>
                </div>
              </div>
              <div className="mt-4 text-center">
                <span className="font-heading text-xl font-bold text-primary-700">Freshhlock</span>
                <p className="text-xs text-foreground-400 mt-0.5">Digital Orchestration Layer</p>
              </div>
            </div>

            {/* Connecting lines from hub to nodes */}
            <div className="hidden md:block absolute top-[140px] left-1/2 -translate-x-1/2 w-[70%] h-[2px]">
              <div className="relative w-full h-full">
                {/* Line to left node */}
                <div className="absolute left-0 top-0 w-[33%] h-full bg-gradient-to-r from-transparent via-secondary-400/40 to-secondary-400/40"></div>
                {/* Line to center node */}
                <div className="absolute left-[33%] top-0 w-[34%] h-full bg-gradient-to-r from-primary-400/40 via-primary-400/40 to-primary-400/40"></div>
                {/* Line to right node */}
                <div className="absolute right-0 top-0 w-[33%] h-full bg-gradient-to-l from-transparent via-accent-400/40 to-accent-400/40"></div>
              </div>
            </div>

            {/* Node cards */}
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {ecosystemNodes.map((node) => (
                <div
                  key={node.title}
                  className={`group relative bg-card-50 rounded-xl border ${node.colorBorder} p-5 md:p-6 text-center shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 card-animate`}
                >
                  {/* Top accent bar */}
                  <div className={`absolute top-0 left-6 right-6 h-1 rounded-full ${node.dotColor} opacity-30 group-hover:opacity-60 transition-opacity`}></div>

                  {/* Stat badge */}
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className={`font-heading text-2xl md:text-3xl font-bold ${node.colorTextBold} tabular-nums`}>
                      {node.stat}
                    </span>
                    <span className="text-xs text-foreground-400 font-medium">{node.statLabel}</span>
                  </div>

                  {/* Icon */}
                  <div className={`${node.color} w-11 h-11 rounded-xl flex items-center justify-center text-paper-50 mx-auto mb-3 animate-icon-float icon-hover-pop`}>
                    <i className={`${node.icon} text-xl`}></i>
                  </div>

                  {/* Title */}
                  <h3 className="font-heading font-bold text-primary-700 text-sm md:text-base mb-1.5">
                    {node.title}
                  </h3>

                  {/* Description */}
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    {node.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom cards: why this model works */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {modelBenefits.map((f) => (
            <div
              key={f.title}
              className={`group ${f.accentBg} rounded-xl border ${f.accentBorder} p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-default card-animate`}
            >
              <div className={`w-12 h-12 rounded-xl ${f.accentIconBg} flex items-center justify-center mb-4 animate-icon-float icon-hover-pop`}>
                <i className={`${f.icon} text-xl ${f.accentIconText}`}></i>
              </div>
              <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                {f.title}
              </h3>
              <p className="text-xs text-foreground-500 leading-relaxed">
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}